<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      :page="pageHeaderConfig"
      :right-side-class="
        'col-xl-4 align-items-end d-flex pb-2 justify-content-end'
      "
    />
    <div class="bg-gray-4 p-1 rounded">
      <board class="mb-4 bg-transparent" :details="boardData" />
      <div class="row mx-3 mb-3 mx-auto">
        <div class="first-row row mx-auto col-12">
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Tipo de Servicio:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.serviceType | empty }}
            </span>
          </div>
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Modalidad:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.modality | empty }}
            </span>
          </div>
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Frecuencia:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.frecuency | empty }}
            </span>
          </div>
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Duración:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.duration | empty }}
            </span>
          </div>
        </div>
        <div class="second-row row mx-auto col-12 mt-3">
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Fecha de Admisión:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.admissionDate | date }}
            </span>
          </div>
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Proveedor:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.provider | empty }}
            </span>
          </div>
          <div class="col">
            <div class="font-weight-normal font-14 color-secondary-6">
              Especialista:
            </div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.specialist | empty }}
            </span>
          </div>
          <div class="col">
            &nbsp;
          </div>
        </div>
      </div>
    </div>

    <validation-observer ref="requestFormRef">
      <div class="row mt-4">
        <div class="col-md-12">
          <section-title has-line title="Información General" />
        </div>
      </div>
      <div class="ml-3">
        <div class="row">
          <div class="col-3 mb-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-date-picker
                v-model="serviceData.requestedDate"
                custom-error-msg="Fecha de Solicitud es requerida."
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Fecha de Solicitud:"
                placeholder="DD/MM/YYYY"
              />
            </validation-provider>
          </div>
          <div class="col-3 mb-3 mt-1">
            <div class="data-label pb-3">Número de Solicitud:</div>
            <span class="font-weight-normal font-16 color-black">
              {{ serviceData.requestNumber }}
            </span>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-md-12">
          <div class="ml-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-text-area
                v-model="commentOrDefault"
                class="mt-3"
                :count-classes="['float-right']"
                custom-error-msg="Favor escribir una observación."
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Información Adicional de la Solicitud:"
                :maxlength="3000"
                show-char-count
              />
            </validation-provider>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-md-12">
          <section-title
            has-line
            title="Descripción del Servicio Compensatorio"
          />
          <request-list
            :is-read-only="isReadOnly"
            :reposition-reasons="absenceReasons"
            :value="serviceInterventions"
          />
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-md-12">
          <section-title
            bg-icon="green"
            class="mb-3"
            icon="fas fa-file-plus"
            reverse
            title="Documentos Añadidos"
          />
          <request-documents-list
            :items="documents"
            @on-download-document="onDownloadFile"
          />
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomDatePicker from 'custom-date-picker';
import CustomTextArea from 'CustomTextArea';
import RequestList from './RequestList.vue';
import {
  getCompensatoryServiceRequestInfo,
  getCompensatoryServiceInterventions,
  getCompensatoryRepositionAbsence,
} from '@/services/provider/compensatoryRequest.api';
import RequestDocumentsList from './RequestDocumentList.vue';
import { getRequestDocumentsByRequestId } from '@/services/provider/extended-academic-year.api';
import { downloadDocumentByAttachmentId } from '@/services/common/document.api.js';

export default {
  name: 'Request',
  props: {
    requestId: {
      type: Number,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
    },
    academicYearId: {
      type: Number,
      default: null,
    },
  },
  components: {
    PageHeader,
    Board,
    SectionTitle,
    CustomDatePicker,
    CustomTextArea,
    RequestList,
    RequestDocumentsList,
  },
  data() {
    return {
      boardData: [],
      serviceData: {},
      serviceInterventions: [],
      absenceReasons: [],
      documents: [],
    };
  },
  computed: {
    pageHeaderConfig() {
      return {
        title: `Solicitud de Reposición`,
        icon: 'fas fa-user-tie',
        routes: [
          'Proveedor',
          'Manejo de Servicios Compensatorios',
          'Solicitud de Reposición',
        ],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success m-1',
          click: this.onReturnHandler,
        },
      ];
    },
    commentOrDefault() {
      return (
        this.serviceData.additionalComment ||
        'Solicitud aprobada como resultado de análisis de balances de servicios documentados a través de MiPE.'
      );
    },
  },
  async mounted() {
    await getCompensatoryServiceRequestInfo(this.requestId).then((res) => {
      this.serviceData = res.data;
      this.mapBoardInfo();
    });

    await getCompensatoryServiceInterventions(this.requestId).then((res) => {
      this.serviceInterventions = res.data;
    });

    await getCompensatoryRepositionAbsence().then((res) => {
      this.absenceReasons = res.data;
    });
    await getRequestDocumentsByRequestId(
      this.academicYearId,
      this.requestId
    ).then((resp) => {
      if (resp.data) {
        resp.data.forEach((d) => {
          if (d.filename) {
            this.documents.push({
              id: d.documentId,
              documentTypeDesc: d.documentTypeName,
              name: d.filename,
            });
          }
        });
      }
    });
  },
  methods: {
    mapBoardInfo() {
      this.boardData = [
        [
          {
            label: 'Estudiante:',
            icon: 'fas fa-address-book',
            data: `${this.serviceData.studentName} - ${this.serviceData.studentSieId}`,
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.serviceData.impediment,
          },
          {
            label: 'Región:',
            icon: 'fas fa-ballot-check',
            data: this.serviceData.region,
          },
          {
            label: 'Municipio:',
            icon: 'fas fa-ballot-check',
            data: this.serviceData.municipality,
          },
          {
            label: 'Escuela:',
            icon: 'fas fa-ballot-check',
            data: this.serviceData.schoolName,
          },
        ],
      ];
    },
    onReturnHandler() {
      this.$router.push({ name: 'servicesCompensatoryManagementSearch' });
    },
    onDownloadFile(data) {
      if (data) {
        return new Promise((resolve, reject) => {
          downloadDocumentByAttachmentId(data.id)
            .then(() => resolve())
            .catch((err) => reject(err));
        });
      }
    },
  },
};
</script>
<style scoped>
.outline-green {
  color: #2db782;
  background-color: #ffffff;
  border-color: #2db782;
}
.educative-info {
  background-color: #ffedbb;
  position: relative;
  bottom: 10px;
  padding: 12px 8px 8px 19px;
  border-radius: 0 0 5px 5px;
  z-index: 1;
}
.educative-info i {
  color: #2db782;
  padding-right: 10px;
}
.educative-info label {
  color: #2db782;
  text-decoration: underline;
}
.educative-info label:hover {
  color: #27a373;
  cursor: pointer;
}
</style>
